/**
 * The original's own map-grid sector code — `"SECTOR E6"` — recovered whole
 * from `RB.EXE` and reproduced here in its own arithmetic.
 *
 * AD-013 (`docs/engineering/sun-blind-spot-design.md` §2.2) found the REAL
 * NAVIGATION flag `ds:0x7277` gating a ~500-byte block at file
 * `0x1fc26`–`0x1fe3e` that synthesises a letter-plus-digit code from world
 * position and draws it. That writeup stopped at "a letter and a digit biased
 * by `+0x41`/`+0x31`"; this lane disassembled the block far enough to take the
 * two constants out of it, which is what lets the port draw a *real* grid
 * rather than an invented one:
 *
 * ```
 * 1fc69: mov  $0x3,%ax          ; the divisor, pushed high word first…
 * 1fc6c: mov  $0xde00,%dx       ; …then low: 0x0003de00 = 253 440 units
 * 1fc71: mov  $0x13,%ax         ; the reference point, same order:
 * 1fc74: mov  $0x5600,%dx       ;   0x00135600 = 1 267 200 units
 * 1fc77: sub  -0x2c(%bp),%dx    ; reference − (second long of the position)
 * 1fc7a: sbb  -0x2a(%bp),%ax
 * 1fc7f: lcall $0x0,$0x2bd5     ; Borland's signed long divide
 * 1fc84: add  $0x41,%al         ; + 'A'
 * ...
 * 1fc91: mov  -0x2e(%bp),%ax    ; (first long of the position) + reference
 * 1fc97: add  $0x5600,%dx
 * 1fc9b: adc  $0x13,%ax
 * 1fca0: lcall $0x0,$0x2bd5
 * 1fca5: add  $0x31,%al         ; + '1'
 * ```
 *
 * The prefix is real too: `1fc59` loads `ds:0x123a`, which against the Borland
 * `DGROUP` base at file `0x47090` is `0x482ca` — the bytes `53 45 43 54 4f 52
 * 20 00`, `"SECTOR "`. The code is `strcat`ed onto it and drawn at `y = 0x8a`.
 *
 * **The two constants make sense of each other.** `253 440` `.WLD` units is
 * `253 440 / 8` feet (`world/wld-units.ts`), i.e. **exactly 6 statute miles** —
 * a sane grid square on the manual's 80-mile regional maps, ~13 squares across.
 * And `1 267 200` is exactly five of those squares. Neither number is rounded
 * to reach that; they were authored in miles.
 *
 * **Which axis is which.** The block copies three `int32`s off the object
 * record and takes the *letter* from the second and the *digit* from the first.
 * `.WLD` stores a placement as `x` then `y`, so the letter runs with `.WLD`
 * `y` and the digit with `.WLD` `x`. Which of those the boxed paper maps
 * printed along their top edge is not recoverable from the corpus and is not
 * guessed here — the port draws the grid the code describes and labels it the
 * way the code labels it.
 *
 * Division is **truncation toward zero**, matching the signed long divide the
 * original calls rather than a floor. The two differ only for a negative
 * quotient, i.e. outside the lettered/numbered part of the grid, and the port
 * reproduces the original's answer there rather than a tidier one.
 */

export const SECTOR_CELL_UNITS = 0x0003de00;
export const SECTOR_REFERENCE_UNITS = 0x00135600;

/**
 * A grid square's side in statute miles — `SECTOR_CELL_UNITS / 8` feet over
 * 5 280, which comes out at exactly **6** with nothing rounded away. It is
 * printed on the sheet because it is the one thing a grid is useless without,
 * and because it is a recovered figure rather than a chosen one.
 */
export const SECTOR_CELL_MILES = SECTOR_CELL_UNITS / 8 / 5280;

/** `"SECTOR "` — `RB.EXE` file offset `0x482ca`, the literal the code is appended to. */
export const SECTOR_PREFIX = "SECTOR ";

const LETTER_BIAS = 0x41;
const DIGIT_BIAS = 0x31;

/** Borland's `ldiv` truncates toward zero; `Math.trunc` is the same rule. */
function cellIndex(numerator: number): number {
  return Math.trunc(numerator / SECTOR_CELL_UNITS);
}

/** A point in raw `.WLD` world units — the frame `.WLD` placements and this grid share. */
export interface WldPoint {
  readonly x: number;
  readonly y: number;
}

export interface SectorCode {
  /** `0` for `A`, counting along the `.WLD` `y` axis away from the reference point. */
  readonly letterIndex: number;
  /** `0` for `1`, counting along the `.WLD` `x` axis away from the reference point. */
  readonly digitIndex: number;
  /** The two characters the original `strcat`s, e.g. `"E6"`. Bare `String.fromCharCode`, so an off-grid index reads as whatever byte the original would have drawn. */
  readonly code: string;
}

export function sectorCodeAt(point: WldPoint): SectorCode {
  const letterIndex = cellIndex(SECTOR_REFERENCE_UNITS - point.y);
  const digitIndex = cellIndex(point.x + SECTOR_REFERENCE_UNITS);
  return {
    letterIndex,
    digitIndex,
    code: String.fromCharCode(LETTER_BIAS + letterIndex) + String.fromCharCode(DIGIT_BIAS + digitIndex),
  };
}

/** `"SECTOR E6"` — the whole string the original assembles and draws. */
export function sectorLabelAt(point: WldPoint): string {
  return SECTOR_PREFIX + sectorCodeAt(point).code;
}

/** The letter for a cell index: `0` is `A`. */
export function sectorLetter(index: number): string {
  return String.fromCharCode(LETTER_BIAS + index);
}

/** The digit for a cell index: `0` is `1`. */
export function sectorDigit(index: number): string {
  return String.fromCharCode(DIGIT_BIAS + index);
}

/** `.WLD` `y` at the middle of the letter cell `index` — where a printed row label goes. */
export function letterCellCentreY(index: number): number {
  return SECTOR_REFERENCE_UNITS - (index + 0.5) * SECTOR_CELL_UNITS;
}

/** `.WLD` `x` at the middle of the digit cell `index`. */
export function digitCellCentreX(index: number): number {
  return (index + 0.5) * SECTOR_CELL_UNITS - SECTOR_REFERENCE_UNITS;
}

/** Every integer from `a` to `b` inclusive, whichever way round they came — the cell indices a bounds pair spans. */
export function cellIndexRange(a: number, b: number): number[] {
  const from = Math.min(a, b);
  const to = Math.max(a, b);
  const indices: number[] = [];
  for (let index = from; index <= to; index += 1) indices.push(index);
  return indices;
}

/**
 * The `.WLD` `y` coordinates of every grid line between `minY` and `maxY` —
 * the boundaries between one letter and the next, i.e. where `letterIndex`
 * steps. Ascending, so a renderer can walk them in draw order.
 */
export function letterGridLines(minY: number, maxY: number): number[] {
  return gridLines(minY, maxY, (index) => SECTOR_REFERENCE_UNITS - index * SECTOR_CELL_UNITS);
}

/** The `.WLD` `x` coordinates of every grid line between `minX` and `maxX` — where `digitIndex` steps. */
export function digitGridLines(minX: number, maxX: number): number[] {
  return gridLines(minX, maxX, (index) => index * SECTOR_CELL_UNITS - SECTOR_REFERENCE_UNITS);
}

/**
 * Walks cell indices outward from the one containing `min` until the line
 * positions leave `max`. Indices are derived from the same reference/cell
 * constants either way, so the two axes' lines stay in lockstep — the grid is
 * square, which is what makes a sector a square.
 */
function gridLines(min: number, max: number, positionOf: (index: number) => number): number[] {
  if (!(max > min)) return [];
  const lines: number[] = [];
  const first = Math.floor((min + SECTOR_REFERENCE_UNITS) / SECTOR_CELL_UNITS);
  const last = Math.ceil((max + SECTOR_REFERENCE_UNITS) / SECTOR_CELL_UNITS);
  for (let index = first; index <= last; index += 1) {
    const position = positionOf(index);
    if (position >= min && position <= max) lines.push(position);
  }
  return lines.sort((a, b) => a - b);
}
